import { IHistoricalOrder } from '@rbi-ctg/menu';

import { DeliveryStatus, IUserOrderFragment } from '../../generated/rbi-graphql';
import { LaunchDarklyFlag, useFlag } from '../../state/launchdarkly';
import { OrderStatus } from '../../state/order';
import { isDelivery } from '../../utils/service-mode';

import { IUseLaunchDarklyCirServiceModesReturn } from './types';

/**
 * Determines if an order is actively being delivered.
 */
export const checkIsDeliveryActive = (order?: IHistoricalOrder | IUserOrderFragment | null) => {
  if (!order) {
    return false;
  }

  const { delivery, cart } = order || {};
  const isOrderDelivery = isDelivery(cart?.serviceMode || null);

  return Boolean(
    delivery &&
      isOrderDelivery &&
      delivery.status !== DeliveryStatus.ORDER_CANCELLED &&
      delivery.status !== DeliveryStatus.ORDER_ABANDONED &&
      delivery.status !== DeliveryStatus.ORDER_DROPPED_OFF
  );
};

/**
 * This lets us know the maximum amount a user can be refunded for a single order.
 */
export const useCheckRefundUnderMaxPrice = (
  order?: IHistoricalOrder | IUserOrderFragment | null
) => {
  const maxRefundPriceCents = useFlag(LaunchDarklyFlag.MAX_CUSTOMER_INITIATED_REFUND_PRICE_CENTS);

  if (!order) {
    return false;
  }

  const { cart } = order;
  const totalCents = cart?.totalCents || 0;

  return totalCents <= maxRefundPriceCents;
};

/**
 *  Provides Launch Darkly Flags for which service modes are enabled.
 */
export const useLaunchDarklyCirServiceModes = (): IUseLaunchDarklyCirServiceModesReturn => ({
  isDeliveryLDEnabled: useFlag(LaunchDarklyFlag.ENABLE_CIR_DELIVERY),
  isDriveThruLDEnabled: useFlag(LaunchDarklyFlag.ENABLE_CIR_DRIVE_THRU),
  isCurbsideLDEnabled: useFlag(LaunchDarklyFlag.ENABLE_CIR_CURBSIDE),
  isTakeoutLDEnabled: useFlag(LaunchDarklyFlag.ENABLE_CIR_TAKEOUT)
});

/**
 * Determines if an order is a delivery order.
 */
export const checkIsOrderDelivery = (order?: IHistoricalOrder | IUserOrderFragment | null) => {
  if (!order) {
    return false;
  }
  const { cart } = order;

  return isDelivery(cart?.serviceMode || null);
};

/**
 * Determines whether an order status is refundable.
 */
export const checkIsOrderStatusRefundable = (
  order?: IHistoricalOrder | IUserOrderFragment | null
) => {
  if (!order) {
    return false;
  }
  const { status } = order;

  return status === OrderStatus.INSERT_SUCCESSFUL;
};

/**
 * Determines whether an order was either refunded or a refund was requested.
 */
export const checkIsOrderStatusRefunded = (
  order?: IHistoricalOrder | IUserOrderFragment | null
) => {
  if (!order) {
    return false;
  }
  const { status } = order;

  // Order statuses are defined in 2 type enums and causes conflict.
  return [OrderStatus.REFUND_SUCCESSFUL as string, OrderStatus.REFUND_REQUESTED as string].includes(
    status
  );
};

/**
 * Used to make sure all conditions are met.
 *
 * Conditions are passed as an array of boolean results.
 *
 * If any condition fails, returns false.
 */
export const checkServiceModeRequirements = (conditions: boolean[]) => conditions.every(Boolean);

/**
 * Verifies that the order's service mode is in the enabled for refund.
 *
 * Not related to launch darkly flags. Only checks if code has the service
 * mode as an available option by checking the service mode requirements list
 * keys.
 */
export const checkServiceModeEnabled = (
  serviceModeRequirements: {},
  order?: IHistoricalOrder | IUserOrderFragment | null
) => Boolean(order && serviceModeRequirements[order?.cart.serviceMode || '']);

/**
 * Scans list of messages and returns first truthy string.
 * returns false if none found.
 */
export const getFirstMessage = (list: (string | false)[]) => list.find(item => item) || false;
